import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useUserContext } from './hooks/useUserContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Box, CircularProgress } from '@mui/material';
// Components (will be created next)
import Home from './pages/Home';
import Login from './pages/auth/Login';
import IndividualSignUpPage from './pages/auth/IndividualSignUp';
import IndividualPlansPage from './pages/billing/IndividualPlans';
import IndividualBackgroundPage from './pages/auth/IndividualBackground';
import IndividualBackgroundManualPage from './pages/auth/IndividualBackgroundManual';
import IndividualBackgroundConfirmationPage from './pages/auth/IndividualBackgroundConfirmation';
import SubscriptionConfirmationPage from './pages/auth/SubscriptionConfirmation';
import BusinessSignUp from './pages/auth/BusinessSignUp';
import BusinessPlansPage from './pages/billing/BusinessPlans';
import BusinessSetupPage from './pages/auth/BusinessSetup';
import BusinessInvitePage from './pages/auth/BusinessInvite';
import Unauthorized from './pages/Unauthorized';
import TenantInactive from './pages/TenantInactive';
import MainLayout from './components/layout/MainLayout';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Profile from './pages/Profile';
import MyLearning from './pages/MyLearning';
import SkillsAssessment from './pages/SkillsAssessment';
import Assistant from './pages/Assistant';
import Faq from './pages/Faq';
import ContactUs from './pages/ContactUs';
import ReleaseNotes from './pages/ReleaseNotes';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import AdminLayout from './pages/system-admin/AdminLayout';
import { TenantAdminLayout } from './pages/tenant-admin';
import BillingRoute from './components/BillingRoute';
import CpeHistory from './pages/CpeHistory';
import CpeHistoryImport from './pages/CpeHistoryImport';
import LicenseManagement from './pages/LicenseManagement';
import Bookmarks from './pages/Bookmarks';
import AcceptTenantInvite from './pages/AcceptTenantInvite';
import FeedbackFab from './components/FeedbackFab';
import Onboarding from './pages/Onboarding';
import OnboardingGate from './components/OnboardingGate';
import { withMarketingParams } from './services/googleAds';

/** `<Navigate replace>` that carries gclid / UTM params through the redirect
 *  so Google Ads can still read the click id after we move the user. */
function MarketingNavigate({ to }: { to: string }) {
  return <Navigate to={withMarketingParams(to)} replace />;
}

// Loading Component
function LoadingScreen() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress size={48} />
    </Box>
  );
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, tenantAccess, loading } = useUserContext();
  const location = useLocation();
  if (!isLoaded || loading) {
    return <LoadingScreen />;
  }
  if (isSignedIn && tenantAccess === 'inactive' && location.pathname !== '/tenant-inactive') {
    return <Navigate to="/tenant-inactive" replace />;
  }
  // Only redirect if not on login or signup page
  if (!isSignedIn && location.pathname !== '/auth/login' && location.pathname !== '/auth/individual-signup') {
    // Preserve the intended destination so Login can return the user after sign-in
    const redirectUrl = encodeURIComponent(location.pathname + location.search);
    return <MarketingNavigate to={`/auth/login?redirect_url=${redirectUrl}`} />;
  }
  return <>{children}</>;
}

// Admin Route Component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, isAdmin, loading } = useUserContext();
  const location = useLocation();
  if (!isLoaded || loading) {
    return <LoadingScreen />;
  }
  if (!isSignedIn && location.pathname !== '/auth/login' && location.pathname !== '/auth/individual-signup') {
    return <Navigate to="/auth/login" replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
}

// Tenant Admin Route Component
function TenantAdminRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, isTenantAdmin, isAdmin, loading } = useUserContext();
  const location = useLocation();
  if (!isLoaded || loading) {
    return <LoadingScreen />;
  }
  if (!isSignedIn && location.pathname !== '/auth/login' && location.pathname !== '/auth/individual-signup') {
    return <Navigate to="/auth/login" replace />;
  }
  if (!isTenantAdmin && !isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
}

// Home or Redirect Component - redirects logged in users to assistant
function HomeOrRedirect() {
  const { isLoaded, isSignedIn, clerkUser, loading, tenantAccess } = useUserContext();
  
  // Debug logging to understand the loading state
  console.log('🏠 HomeOrRedirect render:', { isLoaded, isSignedIn, clerkUser: !!clerkUser, loading });
  
  if (!isLoaded) {
    console.log('🔄 Still loading Clerk authentication...');
    return <LoadingScreen />;
  }
  
  if (isSignedIn) {
    if (tenantAccess === 'inactive') {
      return <Navigate to="/tenant-inactive" replace />;
    }
    console.log('✅ User is signed in, redirecting to assistant');
    return <MarketingNavigate to="/app/assistant" />;
  }
  
  console.log('👋 User not signed in, showing Home page');
  return <Home />;
}


function App() {
  return (
    <BrowserRouter>
        <ThemeProvider>
          <FeedbackFab />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomeOrRedirect />} />
            <Route path="/home" element={<MarketingNavigate to="/" />} />
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/individual-signup" element={<IndividualSignUpPage />} />
            <Route path="/auth/individual-plans" element={<IndividualPlansPage />} />
            <Route path="/auth/individual-background" element={<IndividualBackgroundPage />} />
            <Route path="/auth/individual-background-manual" element={<IndividualBackgroundManualPage />} />
            <Route path="/auth/individual-background-confirmation" element={<IndividualBackgroundConfirmationPage />} />
            <Route path="/auth/business-signup" element={<BusinessSignUp />} />
            <Route path="/auth/business-plans" element={<BusinessPlansPage />} />
            <Route path="/auth/business-setup" element={<BusinessSetupPage />} />
            <Route path="/auth/business-invite" element={<BusinessInvitePage />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/tenant-inactive" element={<TenantInactive />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route
              path="/accept-tenant-invite"
              element={<AcceptTenantInvite />}
            />
            {/* Public: quote recipients land here from Chargebee checkout while
                signed out. The page renders a sign-in prompt when unauthenticated;
                subscription data is gated by the API ([Authorize]), not the route. */}
            <Route
              path="/subscription-confirmation"
              element={<SubscriptionConfirmationPage />}
            />

            {/* Protected routes */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <OnboardingGate>
                    <MainLayout />
                  </OnboardingGate>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/assistant" replace />} />
              <Route path="dashboard" element={<Navigate to="/app/assistant" replace />} />
              <Route path="courses" element={<Courses />} />
              <Route path="courses/:id" element={<CourseDetail />} />
              <Route path="profile" element={<Profile />} />
              <Route path="my-learning" element={<MyLearning />} />
              <Route path="skills-assessment" element={<SkillsAssessment />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="faq" element={<Faq />} />
              <Route path="contact" element={<ContactUs />} />
              <Route path="release-notes" element={<ReleaseNotes />} />
              <Route path="terms" element={<TermsOfService />} />
              <Route path="refund-policy" element={<RefundPolicy />} />
              <Route path="cpe-history" element={<CpeHistory />} />
              <Route path="cpe-history/import" element={<CpeHistoryImport />} />
              <Route path="licenses" element={<LicenseManagement />} />
              <Route path="onboarding" element={<Onboarding />} />
              {/* Legacy routes — compliance pages became license management */}
              <Route path="compliance" element={<Navigate to="/app/licenses" replace />} />
              <Route path="compliance-info" element={<Navigate to="/app/licenses" replace />} />
              <Route path="bookmarks" element={<Bookmarks />} />
              <Route path="billing" element={<BillingRoute />} />

              {/* Admin routes (nested under MainLayout for shared nav) */}
              <Route
                path="system-admin/*"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              />

              {/* Tenant Admin routes (nested under MainLayout for shared nav) */}
              <Route
                path="tenant-admin/*"
                element={
                  <TenantAdminRoute>
                    <TenantAdminLayout />
                  </TenantAdminRoute>
                }
              />
              
            </Route>

            {/* Catch all */}
            <Route path="*" element={<MarketingNavigate to="/" />} />
          </Routes>
        </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
